import { Box, Flex, SimpleGrid, Text, chakra } from "@chakra-ui/react"
import { FormattedMessage } from "react-intl"

import { SHARED_DATA_STATUS } from "@/domain/sharedData/constants"
import type { InterBuStatsJsonldReadItemInterbuStats } from "@/networking/api"

import { getSharedDataStatusDisplay } from "@/components/DomainTag/SharedDataStatusTag/SharedDataStatusTag"

import { getInterBuStatusColors } from "../InterBUStatus/interbuStatusPalette"
import { countOf, getDistribution, getStatusGroups } from "./indicatorRules"

type Props = {
  stats?: InterBuStatsJsonldReadItemInterbuStats
  direction: "received" | "sent" | "all"
  // Status filter of the list, set by a click on a tile (ADR 0009, D1).
  activeStatus?: string
  onStatusFilter: (code?: string) => void
}

// Each status in its colour of the DS InterBU palette (ADR 0009, D1 revised on 2026-10-06); the
// legend keeps the words, colour never carries the meaning alone.
const colorOf = (code: string) => getInterBuStatusColors(code)?.fill ?? "line"

// White card with a red top line (graphic accent, allowed by the brand book), after the mock-up.
const Tile: React.FC<React.PropsWithChildren<{ label?: React.ReactNode }>> = ({
  label,
  children,
}) => (
  <Flex
    direction="column"
    gap="3"
    p="5"
    bg="surface"
    borderTop="2px solid"
    borderColor="accent"
    minW={0}
  >
    {label && <TileLabel>{label}</TileLabel>}
    {children}
  </Flex>
)

const TileLabel: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Text
    as="h4"
    fontSize="3xs"
    fontWeight="semibold"
    letterSpacing="widest"
    textTransform="uppercase"
    color="text"
  >
    {children}
  </Text>
)

// A figure tile: the number first, then its label (mock-up).
const FigureTile: React.FC<{
  label: React.ReactNode
  value: number
  testId: string
  isActionRequired?: boolean
  code: string
  activeStatus?: string
  onStatusFilter: Props["onStatusFilter"]
}> = ({ label, value, testId, isActionRequired = false, code, activeStatus, onStatusFilter }) => (
  <Tile>
    <FilterButton code={code} activeStatus={activeStatus} onStatusFilter={onStatusFilter}>
      <Text
        fontSize="2xl"
        fontWeight="semibold"
        lineHeight="tight"
        color={isActionRequired ? "accent" : "text"}
        data-testid={testId}
        data-action-required={isActionRequired}
      >
        {value}
      </Text>
      <Box mt="2">
        <TileLabel>{label}</TileLabel>
      </Box>
    </FilterButton>
  </Tile>
)

// A clickable figure: applies its status filter, or removes it when it is already applied.
const FilterButton: React.FC<
  React.PropsWithChildren<{
    code: string
    activeStatus?: string
    onStatusFilter: Props["onStatusFilter"]
  }>
> = ({ code, activeStatus, onStatusFilter, children }) => {
  const isActive = activeStatus === code
  return (
    <chakra.button
      type="button"
      textAlign="left"
      w="full"
      aria-pressed={isActive}
      onClick={() => onStatusFilter(isActive ? undefined : code)}
      _hover={{ textDecoration: "underline" }}
      textDecoration={isActive ? "underline" : undefined}
    >
      {children}
    </chakra.button>
  )
}

// Indicators of the InterBU tab (ADR 0009, D1 revised): the whole tab, filters left aside; a click
// on a tile filters the list.
export const InterBUIndicators: React.FC<Props> = ({
  stats,
  direction,
  activeStatus,
  onStatusFilter,
}) => {
  const groups = getStatusGroups(stats?.statusCounts)
  const { total, segments } = getDistribution(groups)
  const inProgress = countOf(stats?.statusCounts, SHARED_DATA_STATUS.ACCEPTED)
  const pending = countOf(stats?.statusCounts, SHARED_DATA_STATUS.ASKED)
  // Red only where an answer is required: pending shares received (D1, D3).
  const pendingIsAction = direction === "received" && pending > 0

  return (
    <Flex direction="column" gap="3">
      {/* The tiles describe the whole current tab, whatever the filters (ADR 0009, D1 revised on
          2026-10-06): they stay put while the list is filtered. Say which tab (option C). */}
      <Text as="h3" fontSize="xs" fontWeight="semibold" data-testid="interbu-indicators-scope">
        <FormattedMessage id={`sharing.interbu.indicators.scope.${direction}`} />
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing="4" data-testid="interbu-indicators">
        <Tile
          label={
            <FormattedMessage
              id="sharing.interbu.indicators.distribution"
              values={{ count: total }}
            />
          }
        >
          <Flex
            h="2.5"
            w="full"
            gap="1px"
            borderRadius="full"
            overflow="hidden"
            bg={total === 0 ? "line" : undefined}
            aria-hidden
          >
            {segments.map(segment => (
              <Box
                key={segment.code}
                w={`${segment.percent}%`}
                bg={colorOf(segment.code)}
                data-testid={`interbu-distribution-${segment.code}`}
              />
            ))}
          </Flex>
          <Flex as="ul" listStyleType="none" direction="column" gap="1.5">
            {groups.map(group => {
              const legend = (
                <Flex alignItems="center" gap="2" fontSize="xs" w="full">
                  <Box boxSize="2" bg={colorOf(group.code)} />
                  <Text as="span" flex="1">
                    <FormattedMessage id={getSharedDataStatusDisplay(group.code).messageId} />
                  </Text>
                  <Text as="span" fontWeight="semibold">
                    {group.count}
                  </Text>
                </Flex>
              )
              return (
                <li key={group.code}>
                  <FilterButton
                    code={group.code}
                    activeStatus={activeStatus}
                    onStatusFilter={onStatusFilter}
                  >
                    {legend}
                  </FilterButton>
                </li>
              )
            })}
          </Flex>
        </Tile>

        <FigureTile
          label={<FormattedMessage id="sharing.interbu.indicators.inProgress" />}
          value={inProgress}
          testId="indicator-in-progress"
          code={SHARED_DATA_STATUS.ACCEPTED}
          activeStatus={activeStatus}
          onStatusFilter={onStatusFilter}
        />

        <FigureTile
          label={<FormattedMessage id="sharing.interbu.indicators.pending" />}
          value={pending}
          testId="indicator-pending"
          isActionRequired={pendingIsAction}
          code={SHARED_DATA_STATUS.ASKED}
          activeStatus={activeStatus}
          onStatusFilter={onStatusFilter}
        />

        <Tile label={<FormattedMessage id="sharing.interbu.indicators.topAgencies" />}>
          {(stats?.topAgencies ?? []).length === 0 ? (
            <Text fontSize="xs" color="textMuted">
              —
            </Text>
          ) : (
            <Flex
              as="ol"
              listStyleType="none"
              direction="column"
              gap="1"
              data-testid="indicator-top-agencies"
            >
              {(stats?.topAgencies ?? []).map(agency => (
                <Flex as="li" key={agency.id} justifyContent="space-between" gap="2" fontSize="xs">
                  <Text as="span" noOfLines={1}>
                    {agency.name}
                  </Text>
                  <Text as="span" fontWeight="semibold">
                    {agency.count}
                  </Text>
                </Flex>
              ))}
            </Flex>
          )}
        </Tile>
      </SimpleGrid>
    </Flex>
  )
}
